import { clientUserText } from '../../../terminology/store';
import { entityLabel } from '../../../terminology/store';
import { useTerminology } from '../../../terminology/TerminologyContext';
import React, { useState, useEffect, useRef, useContext } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import api from '../../services/api';
import ThemeContext from '../../ThemeContext';
import Pagination from '../../Utils/Pagination';
import { SECTION_LABELS, URL_SEGMENTS, buildPath } from '../../../config/Routesconfig';

// ─── Route-derived constants ───
// Page title comes from SECTION_LABELS.users  → "Users"

// Base dashboard prefix for client routes: /:username/dashboard
const dashboardBase = (username) =>
  `/${username}/${URL_SEGMENTS.dashboard}`;

// Add-user absolute path:  /:username/dashboard/user-add
const addUserPath = (username) =>
  `${dashboardBase(username)}/${URL_SEGMENTS.addUser}`;

// Edit-user absolute path:  /:username/dashboard/user-edit/:id
const editUserPath = (username, id) =>
  buildPath(
    `${dashboardBase(username)}/${URL_SEGMENTS.editUser}/:id`,
    { id }
  );

// ─── Component ────────────────
const UserManagement = () => {
  useTerminology();
  const PAGE_TITLE = SECTION_LABELS.users;
  const { theme } = useContext(ThemeContext);
  const { username } = useParams();
  const navigate = useNavigate();

  const fontFamily =
    theme.typography?.fontFamily ||
    '"Roboto", "Helvetica Neue", Arial, sans-serif';

  const bodyFontSize = theme.typography?.bodyFontSize || 14;

  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedUserId, setHighlightedUserId] = useState(null);
  const [highlightedField, setHighlightedField] = useState(null);
  const [hoveredUserId, setHoveredUserId] = useState(null);

  const searchInputRef = useRef(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [gotoPage, setGotoPage] = useState('');

  const [isLoading, setIsLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const [sortField, setSortField] = useState('first_name');
  const [sortOrder, setSortOrder] = useState('asc');

  // FETCH USERS
  useEffect(() => {
    const fetchUsers = async () => {
      setIsLoading(true);
      try {
        const response = await api.get('/client-users');
        setUsers(response.data.data || []);
      } catch (error) {
        toast.error(clientUserText("Failed to fetch users."));
        setUsers([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // SEARCH
  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setHighlightedUserId(null);
    setHighlightedField(null);
    setCurrentPage(1);
  };

  const handleSearchSubmit = () => {
    const matchedUser = filteredUsers.find(
      (user) =>
        user.id.toString() === searchTerm ||
        (user.first_name && user.first_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (user.last_name && user.last_name.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    if (matchedUser) {
      setHighlightedUserId(matchedUser.id);
      if (matchedUser.id.toString() === searchTerm) setHighlightedField('id');
      else if (matchedUser.first_name?.toLowerCase().includes(searchTerm.toLowerCase()))
        setHighlightedField('first_name');
      else if (matchedUser.last_name?.toLowerCase().includes(searchTerm.toLowerCase()))
        setHighlightedField('last_name');
    } else {
      setHighlightedUserId(null);
      setHighlightedField(null);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') handleSearchSubmit();
  };

  // DELETE USER
  const handleDelete = (userId) => {
    setSelectedUserId(userId);
    setShowModal(true);
  };

  const confirmDelete = async () => {
    const id = selectedUserId;
    setIsLoading(true);
    try {
      await api.delete(`/client-users/${id}`);
      setUsers(users.filter((u) => u.id !== id));
      toast.success(clientUserText("User deleted successfully."));
    } catch (error) {
      toast.error(clientUserText("Failed to delete user."));
    } finally {
      setIsLoading(false);
      setShowModal(false);
      setSelectedUserId(null);
    }
  };

  // SORTING
  const handleSort = (field) => {
    if (sortField === field) setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSortValue = (user, field) => {
    if (field === 'id') return user.id;
    const v = user[field] || '';
    return typeof v === 'string' ? v.toLowerCase() : v;
  };

  // FILTER USERS
  const filteredUsers = (users || []).filter(
    (user) =>
      user.id.toString().includes(searchTerm) ||
      user.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.last_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // SORT USERS
  const sortedUsers = filteredUsers.sort((a, b) => {
    const valA = getSortValue(a, sortField);
    const valB = getSortValue(b, sortField);
    if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
    if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const paginatedUsers = sortedUsers.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  return (
    <>
      {/* Loading Spinner */}
      {isLoading && (
        <div
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1050,
          }}
        >
          <div
            style={{
              width: '40px',
              height: '40px',
              border: `4px solid ${theme.colors.primary}`,
              borderTop: '4px solid transparent',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}
          />
        </div>
      )}

      {/* WHITE CARD CONTAINER */}
      <div
        style={{
          backgroundColor: '#ffffff',
          padding: '1rem',
          borderRadius: '10px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          fontFamily,
          marginTop: '10px',
        }}
      >
        {/* HEADER */}
        <div
          style={{
            display: 'flex',
            flexDirection: window.innerWidth < 600 ? 'column' : 'row',
            alignItems: window.innerWidth < 600 ? 'stretch' : 'center',
            justifyContent: 'space-between',
            marginBottom: '1rem',
            gap: '0.5rem',
          }}
        >
          {/*  Title from SECTION_LABELS.users */}
          <h4
            style={{
              fontSize: '14px',
              fontWeight: 'bold',
              marginBottom: 0,
              color: theme.colors.textPrimary,
            }}
          >
            {PAGE_TITLE}
          </h4>

          {/* SEARCH + ADD USER */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              flexWrap: 'wrap',
              justifyContent: window.innerWidth < 600 ? 'stretch' : 'flex-end',
            }}
          >
            {/* Search Field */}
            <div style={{ position: 'relative', maxWidth: '140px', height: '38px' }}>
              <input
                type="text"
                placeholder={`Search ${SECTION_LABELS.users.toLowerCase()}...`}
                value={searchTerm}
                onChange={handleSearch}
                onKeyPress={handleKeyPress}
                ref={searchInputRef}
                style={{
                  width: '100%',
                  height: '100%',
                  border: '1px solid #dee2e6',
                  borderRadius: '25px',
                  outline: 'none',
                  padding: '0 40px 0 14px',
                  fontSize: '12px',
                  backgroundColor: '#ffffff',
                  fontFamily,
                }}
              />
              <button
                onClick={handleSearchSubmit}
                style={{
                  position: 'absolute',
                  right: '2px',
                  top: '2px',
                  width: '34px',
                  height: '34px',
                  background: theme.gradients.primary,
                  border: 'none',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                <i className="bi bi-search" style={{ color: '#fff', fontSize: '14px' }} />
              </button>
            </div>

            {/*  ADD USER BUTTON — path from URL_SEGMENTS.addUser */}
            <Link
              to={addUserPath(username)}
              style={{
                background: theme.gradients.secondary,
                color: theme.colors.textWhite,
                padding: '6px 12px',
                borderRadius: '8px',
                fontSize: 'clamp(10px, 2.5vw, 11px)',
                textDecoration: 'none',
                minWidth: '120px',
                textAlign: 'center',
              }}
            >
              {/*  Label from SECTION_LABELS */}
              Add {SECTION_LABELS.users.replace(/s$/, '')}
            </Link>
          </div>
        </div>

        {/* TOP PAGINATION */}
        <Pagination
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          perPage={perPage}
          setPerPage={setPerPage}
          totalItems={sortedUsers.length}
          gotoPage={gotoPage}
          setGotoPage={setGotoPage}
        />

        {/* TABLE */}
        <div
          style={{
            overflowX: 'auto',
            backgroundColor: '#ffffff',
            borderRadius: '10px',
            border: '1px solid #dee2e6',
            marginTop: '1rem',
            marginBottom: '1rem',
          }}
        >
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 'clamp(12px, 2.5vw, 14px)',
              fontFamily,
            }}
          >
            <thead>
              <tr style={{ backgroundColor: theme.colors.primary, color: theme.colors.textWhite }}>
                <th className="p-2" style={{ cursor: 'pointer' }} onClick={() => handleSort('id')}>ID</th>
                <th className="p-2" style={{ cursor: 'pointer' }} onClick={() => handleSort('first_name')}>First Name</th>
                <th className="p-2" style={{ cursor: 'pointer' }} onClick={() => handleSort('last_name')}>Last Name</th>
                <th className="p-2" style={{ minWidth: '150px' }}></th>
              </tr>
            </thead>

            <tbody>
              {paginatedUsers.length > 0 ? (
                paginatedUsers.map((user) => (
                  <tr
                    key={user.id}
                    onMouseEnter={() => setHoveredUserId(user.id)}
                    onMouseLeave={() => setHoveredUserId(null)}
                    //  Navigate using editUserPath from URL_SEGMENTS
                    onClick={() => navigate(editUserPath(username, user.id))}
                    style={{
                      backgroundColor: user.id === highlightedUserId ? '#FFF3CD' : '#fff',
                      borderBottom: '1px solid #dee2e6',
                      cursor: 'pointer',
                    }}
                  >
                    <td className="p-2">{user.id}</td>
                    <td className="p-2">{user.first_name || 'N/A'}</td>
                    <td className="p-2">{user.last_name || 'N/A'}</td>

                    <td className="p-2 position-relative">
                      {hoveredUserId === user.id && (
                        <div
                          style={{
                            position: 'absolute',
                            right: '0.5rem',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            display: 'flex',
                            gap: '0.5rem',
                          }}
                        >
                          {/*  Edit link from URL_SEGMENTS.editUser */}
                          <Link
                            to={editUserPath(username, user.id)}
                            onClick={(e) => e.stopPropagation()}
                            style={{ color: theme.colors.primary, textDecoration: 'underline' }}
                          >
                            Edit
                          </Link>

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(user.id);
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: theme.colors.danger,
                              textDecoration: 'underline',
                              cursor: 'pointer',
                              padding: 0,
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className="text-center p-3" colSpan={4}>
                    {searchTerm
                      ? `No ${SECTION_LABELS.users.toLowerCase()} found matching "${searchTerm}"`
                      : `No ${SECTION_LABELS.users.toLowerCase()} found.`}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* BOTTOM PAGINATION */}
        <Pagination
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          perPage={perPage}
          setPerPage={setPerPage}
          totalItems={sortedUsers.length}
          gotoPage={gotoPage}
          setGotoPage={setGotoPage}
        />

        {/* DELETE CONFIRMATION MODAL */}
        {showModal && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000,
            }}
          >
            <div
              style={{
                backgroundColor: '#fff',
                padding: '20px',
                borderRadius: '8px',
                width: '320px',
                textAlign: 'center',
              }}
            >
              <h5 style={{ marginBottom: '10px', color: theme.colors.danger }}>
                Confirm Delete
              </h5>
              <p>Are you sure you want to delete this {entityLabel('clientUser', 'singular', SECTION_LABELS.users.replace(/s$/, '').toLowerCase())}?</p>

              <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button className="btn btn-danger" onClick={confirmDelete}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <ToastContainer />
    </>
  );
};

export default UserManagement;